import { Link, type LinkProps } from "@tanstack/react-router";
import { Breadcrumb } from "@/deps/ui-library";
import { cn } from "@/lib/cn";

export interface Crumb {
  label: string;
  to?: LinkProps["to"];
}

/**
 * RDS `Breadcrumb`, composed from its parts rather than `Breadcrumb.Default`: a crumb here may have
 * nowhere to go (a help article's category has no page of its own), and `Default` would make every
 * crumb but the last a router link — a link with no destination, which goes to the current page.
 * The last crumb is the page itself, so RDS renders it as text with `aria-current="page"`.
 */
export function Breadcrumbs({ items }: { items: Crumb[] }) {
  return (
    <Breadcrumb.Root aria-label="Breadcrumb" noCollapse className="text-xs">
      {items.map((crumb, index) => {
        const isLast = index === items.length - 1;
        return (
          <Breadcrumb.Item key={crumb.label + index} isLast={isLast}>
            <Breadcrumb.Link
              as={crumb.to && !isLast ? Link : "span"}
              to={crumb.to && !isLast ? (crumb.to as string) : undefined}
              className={cn(
                "rounded px-1.5 py-1 text-xs",
                isLast
                  ? "text-foreground font-semibold"
                  : crumb.to
                    ? "text-muted-foreground hover:bg-muted hover:text-foreground no-underline"
                    : "text-muted-foreground",
              )}
            >
              {crumb.label}
            </Breadcrumb.Link>
          </Breadcrumb.Item>
        );
      })}
    </Breadcrumb.Root>
  );
}
